import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CheckCircle2, Loader2, QrCode as QrIcon, Smartphone, X } from "lucide-react";
import { toast } from "sonner";

const PAIRING_CODE_REGEX = /^[A-Z0-9]{4}-?[A-Z0-9]{4}$/i;

export default function QRCodeModal({ open, onClose, onConnected, existingChip }) {
  const [chip, setChip] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [progress, setProgress] = useState(0);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [evolutionDown, setEvolutionDown] = useState(false);
  const [instanceMissing, setInstanceMissing] = useState(false);
  const [method, setMethod] = useState("qr"); // "qr" | "code"
  const [pairNumber, setPairNumber] = useState("");
  const [pairCode, setPairCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("Iniciando conexão, aguarde alguns segundos...");

  const timerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const isReal = chip?.wa_mode === "real";

  useEffect(() => {
    if (!open) return;
    setChip(null);
    setQrCode(null);
    setProgress(0);
    setConnected(false);
    setError(null);
    setEvolutionDown(false);
    setInstanceMissing(false);
    setPairCode(null);
    setPairNumber("");
    setLoading(false);

    if (existingChip) {
      setChip(existingChip);
      setQrCode(existingChip.qr_code || null);
      if (existingChip.pairing_code && PAIRING_CODE_REGEX.test(existingChip.pairing_code)) {
        setPairCode(existingChip.pairing_code);
        setMethod("code");
      } else {
        setMethod("qr");
      }
    } else {
      // Novo chip: método padrão QR, inicia criação automática
      setMethod("qr");
      createChipWithMethod("qr", null);
    }

    return () => {
      clearInterval(timerRef.current);
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingChip]);

  const createChipWithMethod = async (chosenMethod, phoneNumber) => {
    setLoading(true);
    setError(null);
    setLoadingMsg(
      chosenMethod === "code"
        ? "Iniciando conexão e gerando código de pareamento..."
        : "Iniciando conexão, aguarde alguns segundos..."
    );

    try {
      const payload = {};
      if (chosenMethod === "code" && phoneNumber) {
        payload.number = phoneNumber;
      }
      const { data } = await api.post("/chips", payload);
      setChip(data.chip);
      if (data.qr_code) setQrCode(data.qr_code);
      if (data.pairing_code && PAIRING_CODE_REGEX.test(data.pairing_code.trim())) {
        setPairCode(data.pairing_code.trim().toUpperCase());
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao criar conexão.");
    } finally {
      setLoading(false);
    }
  };

  // Modo simulação: conecta automaticamente após ~5s
  useEffect(() => {
    if (!chip || !open || connected || isReal) return;
    const started = Date.now();
    timerRef.current = setInterval(async () => {
      const pct = Math.min(100, ((Date.now() - started) / 5000) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(timerRef.current);
        try {
          await api.post(`/chips/${chip.id}/connect`);
          setConnected(true);
          toast.success("Chip conectado! O aquecimento já começou na nuvem.");
          setTimeout(() => {
            onConnected();
            onClose();
          }, 1200);
        } catch (e) {
          setError(formatApiErrorDetail(e.response?.data?.detail));
        }
      }
    }, 100);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chip, open]);

  // Modo real (Evolution API): polling dinâmico (3s sem QR/código, 12s com QR/código)
  useEffect(() => {
    if (!chip || !open || connected || !isReal) return;

    let isMounted = true;

    const poll = async () => {
      try {
        const { data } = await api.get(`/chips/${chip.id}/qr`);
        if (!isMounted) return;

        if (data.instance_missing) {
          setEvolutionDown(false);
          setInstanceMissing(true);
          setQrCode(null);
          return;
        }
        if (data.evolution_down) {
          setEvolutionDown(true);
          setQrCode(null);
        } else {
          setEvolutionDown(false);
          if (data.qr_code) setQrCode(data.qr_code);
        }
        if (data.status === "conectado") {
          setConnected(true);
          toast.success("Chip conectado! O aquecimento já começou na nuvem.");
          setTimeout(() => {
            onConnected();
            onClose();
          }, 1200);
          return;
        }
      } catch (e) {
        if (!isMounted) return;
        setEvolutionDown(true);
        setQrCode(null);
      }

      if (isMounted) {
        // Polling de 3s enquanto não houver QR (e não houver código de pareamento), 12s depois que ele aparecer
        const delay = (qrCode || pairCode) ? 12000 : 3000;
        pollTimerRef.current = setTimeout(poll, delay);
      }
    };

    const initialDelay = (qrCode || pairCode) ? 12000 : 3000;
    pollTimerRef.current = setTimeout(poll, initialDelay);

    return () => {
      isMounted = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [chip, open, isReal, connected, qrCode, pairCode]);

  const requestPairingCode = async () => {
    const digits = pairNumber.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Digite o número com DDI+DDD, ex: 5511987654321");
      return;
    }

    if (!chip) {
      // Se o chip ainda não foi criado, cria já com o número
      await createChipWithMethod("code", digits);
      return;
    }

    setLoading(true);
    setLoadingMsg("Gerando código de pareamento no WhatsApp...");
    try {
      const { data } = await api.post(`/chips/${chip.id}/pairing-code`, { number: digits });
      const receivedCode = data?.pairing_code;
      if (typeof receivedCode === "string" && PAIRING_CODE_REGEX.test(receivedCode.trim())) {
        setPairCode(receivedCode.trim().toUpperCase());
        setMethod("code");
      } else {
        setPairCode(null);
        toast.error("Não foi possível gerar o código agora. Aguarde alguns segundos e tente de novo, ou use o QR Code.");
      }
    } catch (e) {
      setPairCode(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Não foi possível gerar o código agora. Aguarde alguns segundos e tente de novo, ou use o QR Code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900/95 backdrop-blur-md border-slate-800 text-slate-100 sm:max-w-md w-full max-w-[95vw] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-slate-100">
            {connected ? "Chip conectado!" : "Conectar WhatsApp"}
          </DialogTitle>
          <button
            onClick={onClose}
            data-testid="qr-modal-close-button"
            className="absolute right-4 top-4 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>

        {!connected && !instanceMissing && !error && (
          <div className="flex rounded-lg bg-slate-800/80 p-1 border border-slate-700/60 mb-1">
            <button
              type="button"
              onClick={() => setMethod("qr")}
              data-testid="tab-qr"
              className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 rounded-md text-xs font-medium transition-all ${
                method === "qr"
                  ? "bg-emerald-500 text-slate-950 shadow-sm"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <QrIcon className="w-3.5 h-3.5" />
              QR Code
            </button>
            <button
              type="button"
              onClick={() => setMethod("code")}
              data-testid="tab-code"
              className={`flex-1 flex items-center justify-center gap-2 py-1.5 px-3 rounded-md text-xs font-medium transition-all ${
                method === "code"
                  ? "bg-emerald-500 text-slate-950 shadow-sm"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <Smartphone className="w-3.5 h-3.5" />
              Código de Pareamento
            </button>
          </div>
        )}

        {error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-300 break-words max-w-full" data-testid="qr-modal-error">
            {error}
          </div>
        ) : instanceMissing ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-sm text-amber-300 text-center break-words max-w-full" data-testid="qr-modal-instance-missing">
            Este chip não existe mais no servidor de conexão. Remova o chip e conecte novamente.
          </div>
        ) : connected ? (
          <div className="flex flex-col items-center py-10 gap-3">
            <CheckCircle2 className="w-14 h-14 text-emerald-400" />
            <p className="text-slate-200 font-semibold">Sessão ativa na nuvem</p>
            <p className="text-sm text-slate-400 text-center">O loop de aquecimento continua rodando mesmo com o navegador fechado.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 w-full">
            {/* Espaço central de conexão: nunca fica vazio */}
            {method === "qr" ? (
              <div className="flex flex-col items-center gap-3 w-full">
                <div className="relative p-3 bg-white rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.2)] flex items-center justify-center min-w-[208px] min-h-[208px]">
                  {qrCode ? (
                    <>
                      <img src={qrCode} alt="QR Code WhatsApp" className="w-52 h-52 object-contain" data-testid="qr-code-canvas" />
                      <div
                        className={`absolute left-0 right-0 h-0.5 bg-emerald-400/80 shadow-[0_0_12px_rgba(16,185,129,0.8)] ${isReal ? "animate-pulse top-1/2" : "transition-all duration-100"}`}
                        style={isReal ? {} : { top: `${progress}%` }}
                      />
                    </>
                  ) : (
                    <div className="w-52 h-52 flex flex-col items-center justify-center gap-3 bg-slate-900 rounded-lg p-3 text-center">
                      <Loader2 className={`w-8 h-8 animate-spin ${evolutionDown ? "text-amber-400" : "text-emerald-400"}`} />
                      <p className="text-xs text-slate-300 font-medium break-words leading-relaxed" data-testid="qr-modal-generating">
                        {evolutionDown
                          ? "Servidor de conexão instável, tentando novamente..."
                          : loadingMsg}
                      </p>
                    </div>
                  )}
                </div>
                {isReal ? (
                  <div
                    className={`flex items-center gap-2 text-xs ${evolutionDown ? "text-amber-300" : "text-emerald-300"} text-center`}
                    data-testid="qr-modal-waiting-real"
                  >
                    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                    <span>
                      {evolutionDown
                        ? "Servidor de conexão instável, aguarde..."
                        : qrCode ? "Aguardando leitura do QR Code..." : "Iniciando conexão, aguarde alguns segundos..."}
                    </span>
                  </div>
                ) : (
                  <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-emerald-500 h-1.5 transition-all duration-100" style={{ width: `${progress}%` }} />
                  </div>
                )}
                <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside w-full bg-slate-800/30 p-2.5 rounded-lg border border-slate-800">
                  <li>Abra o WhatsApp no celular</li>
                  <li>Toque em <span className="text-slate-200 font-medium">Aparelhos conectados</span></li>
                  <li>Aponte a câmera para o QR Code</li>
                </ol>
                <button
                  type="button"
                  onClick={() => setMethod("code")}
                  data-testid="btn-switch-to-code"
                  className="text-xs text-emerald-400 hover:text-emerald-300 underline font-medium"
                >
                  Prefere conectar digitando um código?
                </button>
              </div>
            ) : (
              /* Modo Código de Pareamento */
              <div className="w-full flex flex-col items-center gap-3">
                {loading ? (
                  <div className="w-full h-44 flex flex-col items-center justify-center gap-3 bg-slate-800/50 border border-slate-700/60 rounded-xl p-4 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
                    <p className="text-xs text-slate-300 font-medium break-words leading-relaxed">
                      {loadingMsg}
                    </p>
                  </div>
                ) : pairCode ? (
                  <div className="w-full bg-slate-800/60 border border-emerald-500/30 rounded-xl p-5 text-center space-y-3 shadow-[0_0_20px_rgba(16,185,129,0.1)]">
                    <p className="text-xs text-slate-400">Digite este código no WhatsApp (válido por ~60s):</p>
                    <p className="text-3xl font-extrabold tracking-[0.3em] text-emerald-300 font-mono max-w-full break-all select-all py-1" data-testid="pairing-code-value">
                      {pairCode}
                    </p>
                    <div className="bg-slate-900/60 rounded-lg p-2.5 text-left border border-slate-800">
                      <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside">
                        <li>Abra o WhatsApp no celular</li>
                        <li>Toque em <span className="text-slate-200 font-medium">Aparelhos conectados</span></li>
                        <li>Toque em <span className="text-slate-200 font-medium">Conectar com número de telefone</span></li>
                        <li>Insira o código de 8 dígitos acima</li>
                      </ol>
                    </div>
                    <div className="flex justify-between items-center pt-1">
                      <button
                        type="button"
                        onClick={() => setPairCode(null)}
                        data-testid="btn-regenerate-pairing"
                        className="text-xs text-slate-400 hover:text-slate-200 underline"
                      >
                        Gerar novo código
                      </button>
                      <button
                        type="button"
                        onClick={() => setMethod("qr")}
                        className="text-xs text-emerald-400 hover:text-emerald-300 underline"
                      >
                        Usar QR Code
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="w-full bg-slate-800/40 border border-slate-700/60 rounded-xl p-4 space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-xs text-slate-300 font-medium">
                        Número do WhatsApp (com DDI e DDD):
                      </label>
                      <div className="flex gap-2">
                        <input
                          value={pairNumber}
                          onChange={(e) => setPairNumber(e.target.value)}
                          placeholder="5511987654321"
                          inputMode="numeric"
                          data-testid="pairing-number-input"
                          className="flex-1 bg-slate-800/80 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                        />
                        <button
                          type="button"
                          onClick={requestPairingCode}
                          disabled={loading || !pairNumber.trim()}
                          data-testid="btn-generate-pairing"
                          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-slate-950 text-sm font-semibold rounded-lg transition-all flex items-center justify-center shrink-0"
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gerar"}
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-500">
                        Exemplo: 5511987654321 (55 = Brasil, 11 = DDD, seguido do número).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <p className="text-[11px] text-slate-500 text-center max-w-full break-words">
              {isReal
                ? "Conexão direta via Evolution API na nuvem. A sessão permanece conectada mesmo com o computador desligado."
                : "Modo demonstração: a conexão é simulada automaticamente em alguns segundos."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
